/**
 * Custom hooks for Bookshelf feature
 */

import { useState, useCallback, useMemo } from 'react';
import * as bookHelpers from '../utils/bookHelpers';

/**
 * Hook for managing filter state
 */
export function useFilters(books) {
  const [filters, setFilters] = useState({
    genres: [],
    status: [],
    rating: null,
    isFavoriteOnly: false,
    searchQuery: '',
  });

  const updateGenreFilter = useCallback((genre, checked) => {
    setFilters(prev => ({
      ...prev,
      genres: checked
        ? [...prev.genres, genre]
        : prev.genres.filter(g => g !== genre),
    }));
  }, []);

  const updateStatusFilter = useCallback((status, checked) => {
    setFilters(prev => ({
      ...prev,
      status: checked
        ? [...prev.status, status]
        : prev.status.filter(s => s !== status),
    }));
  }, []);

  const setRatingFilter = useCallback((rating) => {
    setFilters(prev => ({ ...prev, rating }));
  }, []);

  const setFavoritesFilter = useCallback((isFavoriteOnly) => {
    setFilters(prev => ({ ...prev, isFavoriteOnly }));
  }, []);

  const setSearchQuery = useCallback((query) => {
    setFilters(prev => ({ ...prev, searchQuery: query }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({
      genres: [],
      status: [],
      rating: null,
      isFavoriteOnly: false,
      searchQuery: '',
    });
  }, []);

  const filteredBooks = useMemo(() => {
    let result = [...books];

    if (filters.genres.length > 0) {
      result = bookHelpers.filterByGenre(result, filters.genres);
    }

    if (filters.status.length > 0) {
      result = bookHelpers.filterByStatus(result, filters.status);
    }

    if (filters.rating !== null) {
      result = bookHelpers.filterByMinRating(result, filters.rating);
    }

    if (filters.isFavoriteOnly) {
      result = bookHelpers.filterFavorites(result);
    }

    if (filters.searchQuery) {
      result = bookHelpers.searchBooks(result, filters.searchQuery);
    }

    return result;
  }, [books, filters]);

  return {
    filters,
    setFilters,
    updateGenreFilter,
    updateStatusFilter,
    setRatingFilter,
    setFavoritesFilter,
    setSearchQuery,
    clearAllFilters,
    filteredBooks,
  };
}

/**
 * Hook for managing book sorting
 */
export function useSortBooks(books) {
  const [sortBy, setSortBy] = useState('dateAdded');

  const sortedBooks = useMemo(() => {
    switch (sortBy) {
      case 'dateAdded':
        return bookHelpers.sortByDateAdded(books);
      case 'title':
        return bookHelpers.sortByTitle(books);
      case 'author':
        return bookHelpers.sortByAuthor(books);
      case 'rating':
        return bookHelpers.sortByRating(books);
      case 'progress':
        return bookHelpers.sortByProgress(books);
      case 'dateStarted':
        return [...books].sort((a, b) => (b.dateStarted ?? 0) - (a.dateStarted ?? 0));
      default:
        return books;
    }
  }, [books, sortBy]);

  return {
    sortBy,
    setSortBy,
    sortedBooks,
  };
}

/**
 * Hook for managing book statistics
 */
export function useBookStats(books) {
  const stats = useMemo(() => {
    return {
      tbr: books.filter(b => b.status === 'tbr').length,
      currentlyReading: books.filter(b => b.status === 'reading').length,
      finished: books.filter(b => b.status === 'finished').length,
      favorites: books.filter(b => b.isFavorite).length,
      total: books.length,
    };
  }, [books]);

  return stats;
}

/**
 * Hook for managing modal state
 */
export function useModalState(initialState = false) {
  const [isOpen, setIsOpen] = useState(initialState);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  return [isOpen, open, close, toggle];
}

/**
 * Hook for managing pagination
 */
export function usePagination(items, itemsPerPage = 12) {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(items.length / itemsPerPage);

  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  }, [items, currentPage, itemsPerPage]);

  const goToPage = useCallback(
    (page) => {
      const pageNum = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(pageNum);
    },
    [totalPages]
  );

  const goToNextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const goToPreviousPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const reset = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    currentPage,
    totalPages,
    currentItems,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    reset,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
  };
}

/**
 * Hook for managing selected book
 */
export function useSelectedBook() {
  const [selectedBook, setSelectedBook] = useState(null);

  const selectBook = useCallback((book) => {
    setSelectedBook(book);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedBook(null);
  }, []);

  return {
    selectedBook,
    selectBook,
    clearSelection,
  };
}
